import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useCartStore } from "../store";
import type { ShippingAddress } from "../types";

const EMPTY: ShippingAddress = {
	name: "",
	addressLine1: "",
	city: "",
	postalCode: "",
	country: "",
};

export function Checkout() {
	const lines = useCartStore((s) => s.lines);
	const placeOrder = useCartStore((s) => s.placeOrder);
	const navigate = useNavigate();
	const [form, setForm] = useState<ShippingAddress>(EMPTY);
	const submittingRef = useRef(false);

	useEffect(() => {
		if (lines.length === 0 && !submittingRef.current) {
			navigate("/cart", { replace: true });
		}
	}, [lines.length, navigate]);

	const setField = (key: keyof ShippingAddress) => (value: string) =>
		setForm((prev) => ({ ...prev, [key]: value }));

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		submittingRef.current = true;
		const order = placeOrder(form);
		navigate(`/order/${order.id}`, { replace: true });
	};

	const fields: Array<{
		key: keyof ShippingAddress;
		label: string;
		autoComplete: string;
	}> = [
		{ key: "name", label: "Full name", autoComplete: "name" },
		{
			key: "addressLine1",
			label: "Address",
			autoComplete: "address-line1",
		},
		{ key: "city", label: "City", autoComplete: "address-level2" },
		{ key: "postalCode", label: "Postal code", autoComplete: "postal-code" },
		{ key: "country", label: "Country", autoComplete: "country-name" },
	];

	return (
		<section className="max-w-xl mx-auto">
			<h1 className="text-3xl font-bold text-slate-900">Checkout</h1>
			<p className="mt-2 text-slate-600">
				Shipping address for your order.
			</p>
			<form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
				{fields.map((field) => (
					<label
						key={field.key}
						className="flex flex-col gap-1 text-sm"
					>
						<span className="text-slate-700">{field.label}</span>
						<input
							type="text"
							value={form[field.key]}
							onChange={(event) =>
								setField(field.key)(event.target.value)
							}
							data-testid={`checkout-${field.key}`}
							autoComplete={field.autoComplete}
							required
							className="rounded-md border border-slate-300 px-3 py-2"
						/>
					</label>
				))}
				<button
					type="submit"
					data-testid="checkout-place-order"
					className="btn-primary mt-2"
				>
					Place order
				</button>
			</form>
		</section>
	);
}

export default Checkout;
